/**
 * MyFeaturesPage — shows the org's active feature set with upgrade CTAs for locked features.
 * Route: /features (or embedded in Settings)
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Check, Lock, Zap, RefreshCw } from 'lucide-react';

const CATEGORY_ORDER = [
  'Core POS', 'Transactions', 'Inventory', 'Purchasing',
  'Finance', 'Management', 'Communications',
  'Multi-Branch', 'BIR Compliance', 'Reports & Audit', 'Security',
];

const PLAN_LABELS = {
  basic: 'Starter', standard: 'Business', pro: 'Professional', enterprise: 'Enterprise',
};

const UPGRADE_COLORS = {
  standard: '#477242', pro: '#C85A32', enterprise: '#2D3728',
};

export default function MyFeaturesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLocked, setShowLocked] = useState(true);

  const effectivePlan = user?.subscription?.effective_plan || user?.subscription?.plan || 'trial';
  const planLabel = PLAN_LABELS[effectivePlan] || effectivePlan;
  const features = user?.subscription?.features || {};

  useEffect(() => {
    api.get('/organizations/feature-matrix')
      .then(r => { setMatrix(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <RefreshCw size={24} className="animate-spin text-slate-400" />
    </div>
  );

  const defs = matrix?.feature_definitions?.filter(d => !d.always_on) || [];
  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    const catDefs = defs.filter(d => d.category === cat);
    if (catDefs.length) acc[cat] = catDefs;
    return acc;
  }, {});
  // Catch any uncategorized
  defs.forEach(d => {
    if (!CATEGORY_ORDER.includes(d.category)) {
      if (!grouped[d.category]) grouped[d.category] = [];
      grouped[d.category].push(d);
    }
  });

  const activeFeatCount = defs.filter(d => features[d.id]).length;
  const totalFeatCount = defs.length;

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-8" data-testid="my-features-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[22px] font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text-primary)' }}>
            My Features
          </h2>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
            Features active on your <strong className="text-slate-700">{planLabel}</strong> plan
            · {activeFeatCount}/{totalFeatCount} features enabled
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer select-none">
            <input type="checkbox" checked={showLocked} onChange={e => setShowLocked(e.target.checked)} />
            Show locked features
          </label>
          {effectivePlan !== 'enterprise' && (
            <button
              onClick={() => navigate('/upgrade')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white"
              style={{ backgroundColor: '#1A4D2E' }}
              data-testid="my-features-upgrade-btn"
            >
              <Zap size={11} /> Upgrade Plan
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="rounded-2xl border p-4 space-y-2" style={{ borderColor: 'var(--border-default)' }}>
        <div className="flex justify-between items-center">
          <span className="text-xs font-semibold text-slate-600">{planLabel} Plan — {activeFeatCount} of {totalFeatCount} features active</span>
          <span className="text-xs font-bold text-slate-800">{Math.round((activeFeatCount / totalFeatCount) * 100)}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${(activeFeatCount / totalFeatCount) * 100}%`, backgroundColor: '#1A4D2E' }}
          />
        </div>
        {effectivePlan !== 'enterprise' && (
          <p className="text-xs text-slate-400">
            Upgrade to Enterprise to unlock all {totalFeatCount - activeFeatCount} remaining features.
          </p>
        )}
      </div>

      {/* Feature groups */}
      {Object.entries(grouped).map(([cat, catDefs]) => {
        const hasActive = catDefs.some(d => features[d.id]);
        const hasLocked = catDefs.some(d => !features[d.id]);
        if (!showLocked && !hasActive) return null;

        return (
          <div key={cat} className="rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border-default)' }}>
            <div className="px-4 py-2.5" style={{ backgroundColor: '#F1F0ED', borderBottom: '1px solid var(--border-default)' }}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{cat}</p>
            </div>
            <div className="divide-y" style={{ divideColor: 'var(--border-default)' }}>
              {catDefs.map(feat => {
                const isActive = !!features[feat.id];
                if (!showLocked && !isActive) return null;
                const upgradePlan = feat.upgrade_hint || 'standard';
                const upgradeLabel = PLAN_LABELS[upgradePlan] || upgradePlan;
                const upgradeColor = UPGRADE_COLORS[upgradePlan] || '#477242';

                return (
                  <div
                    key={feat.id}
                    className={`flex items-center justify-between px-4 py-3 ${!isActive ? 'opacity-60' : ''}`}
                    style={{ borderBottom: '1px solid var(--border-default)' }}
                    data-testid={`feature-row-${feat.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                        style={{ backgroundColor: isActive ? '#dcfce7' : '#f1f0ed' }}
                      >
                        {isActive
                          ? <Check size={11} style={{ color: '#1A4D2E' }} />
                          : <Lock size={10} style={{ color: '#9ca3af' }} />
                        }
                      </div>
                      <span className="text-sm font-medium text-slate-700">{feat.label}</span>
                      {feat.depends_on?.length > 0 && (
                        <span className="text-[9px] text-slate-400 hidden sm:block">
                          requires: {feat.depends_on.join(', ')}
                        </span>
                      )}
                    </div>
                    {isActive ? (
                      <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Active</span>
                    ) : (
                      <button
                        onClick={() => navigate('/upgrade')}
                        className="text-[10px] font-bold px-2.5 py-0.5 rounded-full text-white whitespace-nowrap"
                        style={{ backgroundColor: upgradeColor }}
                      >
                        {upgradeLabel} plan →
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
